import { z } from 'zod';
import { AdsId } from './ads-builder.js';
/*
 * «Кампании» кабинета (`/api/ads/accounts/:id/campaigns…`, контракт A1/A2a
 * 03.11.2026): список-дерево, проверка перед паузой/включением, пауза и
 * включение одного объекта и пачкой кампаний. Деньги — центы валюты кабинета
 * (`*Minor`). Схемы `z.object` — лишнее из auto-ads на выходе admin-api
 * отбрасывается; одни и те же схемы разбирают ответ auto-ads в клиенте
 * `@wit-support/auto-ads` (бэкенд A1 пишется параллельно, поэтому
 * необязательные поля мягкие).
 */
/** Meta-id объекта: только цифры. */
export const AdsEntityId = z.string().regex(/^\d{1,30}$/, { message: 'Неверный id объекта' });
export const ADS_CAMPAIGNS_MAX_PAGE_SIZE = 100;
export const ADS_CAMPAIGNS_DEFAULT_PAGE_SIZE = 50;
export const ADS_CAMPAIGNS_BULK_MAX = 50;
export const ADS_CAMPAIGNS_GRACE_MAX_USD = 10_000;
export const ADS_CAMPAIGN_REASON_MAX = 500;
export const ADS_CAMPAIGN_STATES = [
    'all',
    'running',
    'active',
    'paused',
    /** Вкладка «Требуют внимания» (A3 v2): ошибка Meta, «включена, но внутри всё на паузе», запуск требует внимания. */
    'attention',
    /** Вкладка «Архив»: только архивные кампании (A3). */
    'archived',
];
export const AdsCampaignState = z.enum(ADS_CAMPAIGN_STATES);
export const ADS_CAMPAIGN_ORIGINS = ['launch', 'duplicate', 'scale', 'copy', 'other'];
export const AdsCampaignOrigin = z.enum(ADS_CAMPAIGN_ORIGINS);
export const ADS_CAMPAIGN_SORTS = [
    'default',
    'spendToday',
    'name',
    'created',
    // A3 v2: по колонкам таблицы (пустые значения — внизу при любом направлении)
    'status',
    'pauseDays',
    'cppD1',
    'cppD7',
    'cppD30',
    'cppD90',
    'budget',
    'purchases',
    'cppToday',
    'payback',
];
export const AdsCampaignSort = z.enum(ADS_CAMPAIGN_SORTS);
/** Дети кампании за день (`YYYY-MM-DD`); нет — сегодня. */
export const AdsCampaignChildrenQuery = z.object({
    day: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'День — ГГГГ-ММ-ДД' })
        .optional(),
});
export const AdsCampaignListQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(ADS_CAMPAIGNS_MAX_PAGE_SIZE)
        .default(ADS_CAMPAIGNS_DEFAULT_PAGE_SIZE),
    q: z
        .string()
        .trim()
        .max(100)
        .optional()
        .transform((v) => (v ? v : undefined)),
    state: AdsCampaignState.default('all'),
    origin: AdsCampaignOrigin.optional(),
    /** Без `sort`/`dir` параметры в auto-ads не уходят — умолчание `default` на его стороне. */
    sort: AdsCampaignSort.optional(),
    dir: z.enum(['asc', 'desc']).optional(),
    archived: z
        .enum(['true', 'false', '1', '0'])
        .optional()
        .transform((v) => v === 'true' || v === '1'),
    /** День `YYYY-MM-DD` (часовой пояс кабинета): расход, покупки и цена «за день». Нет — сегодня. */
    day: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'День — ГГГГ-ММ-ДД' })
        .optional(),
    /** Только кампании с расходом за 7 закрытых дней. */
    spent7d: z
        .enum(['true', 'false', '1', '0'])
        .optional()
        .transform((v) => v === 'true' || v === '1'),
});
const nstr = z
    .string()
    .nullish()
    .transform((v) => v ?? null);
// Деньги и другие «может не быть» числа: мусор и отсутствие — null («—» на экране), не выдуманный 0.
const nnum = z.number().nullable().catch(null);
/** Кампания/набор/объявление, созданные запуском конструктора. */
export const AdsManagedByLaunch = z
    .object({
    launchId: z.string().min(1),
    batchId: nstr.optional(),
    availableActions: z.array(z.string()).catch([]),
})
    .nullish()
    .transform((v) => v ?? null);
const Spend2 = z.object({
    todayMinor: nnum,
    yesterdayMinor: nnum,
});
const nbool = (d) => z.boolean().catch(d);
/** Цена покупки по окнам (минорные единицы): вчера, 7, 30, 90 дней. */
const CppWindows = z
    .object({ d1: nnum, d7: nnum, d30: nnum, d90: nnum })
    .nullish()
    .transform((v) => v ?? null)
    .catch(null);
/** Покупки за день по цене триала: «триал $1 — 118, $5 — 24». */
const TrialBreakdown = z
    .array(z.object({ trialPriceMinor: z.number(), count: z.number() }))
    .catch([]);
export const ADS_PAUSED_REASON_KINDS = [
    'rule',
    'manual',
    'meta-error',
    'budget-limit',
    'launch',
];
/** Почему на паузе. `text` — английский текст auto-ads: наружу не идёт, админка берёт слово по `kind`. */
const PausedReason = z
    .object({
    kind: z.enum(ADS_PAUSED_REASON_KINDS).catch('manual'),
    text: z.string().catch(''),
    by: z.string().optional().catch(undefined),
    at: z.string().optional().catch(undefined),
})
    .nullish()
    .transform((v) => v ?? null)
    .catch(null);
export const ADS_MATCHED_IN = ['campaign', 'adset', 'ad'];
const MatchedIn = z.enum(ADS_MATCHED_IN).nullable().catch(null);
/** «Вернётся на 1 $» (A4): прогноз выручки на доллар расхода, из «Окупаемости». */
const Payback = z
    .object({
    perDollar: nnum,
    confidence: z.enum(['low', 'ok']).nullable().catch(null),
    note: z.enum(['no-purchases', 'little-data', 'new']).nullable().catch(null),
})
    .nullish()
    .transform((v) => v ?? null)
    .catch(null);
const PurchasesDay = z
    .object({
    today: z.number().catch(0),
    yesterday: z.number().catch(0),
    trialBreakdown: TrialBreakdown,
})
    .catch({ today: 0, yesterday: 0, trialBreakdown: [] });
/** Поля A3 v2, общие для кампании, группы и объявления; каждое — необязательное (auto-ads до A3 их не шлёт). */
const DayFields = {
    pauseDays: nnum,
    cpp: CppWindows,
    cppTodayMinor: nnum,
    pausedReason: PausedReason,
    matchedIn: MatchedIn,
};
export const AdsCampaignRow = z.object({
    campaignId: z.string().min(1),
    name: z.string().catch(''),
    status: z.string().catch(''),
    effectiveStatus: nstr,
    running: z.boolean().catch(false),
    budget: z
        .object({
        level: z.enum(['campaign', 'adset']).nullable().catch(null),
        dailyMinor: nnum,
        lifetimeMinor: nnum,
    })
        .catch({ level: null, dailyMinor: null, lifetimeMinor: null }),
    spend: Spend2.catch({ todayMinor: null, yesterdayMinor: null }),
    purchases: PurchasesDay,
    counts: z
        .object({
        adSets: z.number().catch(0),
        adSetsActive: z.number().catch(0),
        ads: z.number().catch(0),
        adsActive: z.number().catch(0),
    })
        .catch({ adSets: 0, adSetsActive: 0, ads: 0, adsActive: 0 }),
    origin: z
        .object({
        kind: AdsCampaignOrigin.catch('other'),
        launchId: z.string().optional(),
        batchId: z.string().optional(),
        sourceCampaignId: z.string().optional(),
        sourceCampaignName: z.string().optional(),
    })
        .catch({ kind: 'other' }),
    managedByLaunch: AdsManagedByLaunch,
    pendingEnable: z
        .object({
        at: z.string(),
        kind: z.enum(['scheduled-enable', 'rule']).nullable().catch(null),
    })
        .nullish()
        .transform((v) => v ?? null),
    createdAt: nstr,
    lastSyncedAt: nstr,
    ...DayFields,
    /** Включена, но все группы внутри на паузе. */
    allChildrenPaused: nbool(false),
    /** Только admin-api: склейка с «Окупаемостью»; нет — колонка пустая. */
    payback: Payback,
});
const nullObj = (shape) => shape
    .nullish()
    .transform((v) => v ?? null)
    .catch(null);
/** Итоги экрана (A3 v2): плитки KPI-ряда; любое число может отсутствовать. */
export const AdsCampaignListSummary = nullObj(z.object({
    spendTodayMinor: nnum,
    spendYesterdaySameHourMinor: nnum,
    purchasesToday: nnum,
    trialBreakdown: TrialBreakdown,
    cppTodayMinor: nnum,
    cppYesterdayMinor: nnum,
    cppD7Minor: nnum,
    running: nnum,
    total: nnum,
    adSetsRunning: nnum,
    adsRunning: nnum,
    paused: nnum,
    pausedBuckets: z
        .object({ d0_3: nnum, d4_5: nnum })
        .nullish()
        .transform((v) => v ?? null)
        .catch(null),
    graces: nnum,
    timers: nnum,
    nextTimerAt: nstr.catch(null),
}));
/** Счётчики вкладок. */
export const AdsCampaignListCounts = nullObj(z.object({
    all: nnum,
    runningAndPaused: nnum,
    running: nnum,
    paused: nnum,
    archived: nnum,
    attention: nnum,
}));
export const AdsCampaignListResponse = z.object({
    items: z.array(AdsCampaignRow),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    accountSyncedAt: nstr,
    currency: z.string().catch('USD'),
    /** День, за который посчитаны расход/покупки/цена (`YYYY-MM-DD`). */
    day: nstr,
    /** Последний закрытый день, по которому посчитаны окна цены покупки. */
    cppAnchorDay: nstr,
    summary: AdsCampaignListSummary,
    counts: AdsCampaignListCounts,
    freshness: nullObj(z.object({ spendAt: nstr.catch(null), statusesAt: nstr.catch(null) })),
    /** Только admin-api: `false` — «Окупаемость» не ответила, колонка «Вернётся на 1 $» пуста с подсказкой. */
    paybackAvailable: z.boolean().catch(true),
    /**
     * Возможности auto-ads (рубильник `CAMPAIGN_OPS_LAUNCH_DELEGATION_ENABLED`): без
     * `launchDelegation` объекты запуска не ставятся на паузу и не включаются отсюда.
     * Нет поля — false.
     */
    capabilities: z
        .object({ launchDelegation: z.boolean().catch(false) })
        .nullish()
        .transform((v) => v ?? { launchDelegation: false })
        .catch({ launchDelegation: false }),
    /** Только admin-api: сортировка по окупаемости сделана по первым 2000 кампаниям (их больше). */
    paybackSortCapped: z.boolean().catch(false),
});
export const AdsCampaignAd = z.object({
    adId: z.string().min(1),
    name: z.string().catch(''),
    status: z.string().catch(''),
    effectiveStatus: nstr,
    spend: z.object({ todayMinor: nnum }).catch({ todayMinor: null }),
    managedByLaunch: AdsManagedByLaunch,
    thumbnailUrl: nstr,
    purchases: PurchasesDay,
    ...DayFields,
});
export const AdsCampaignAdSet = z.object({
    adSetId: z.string().min(1),
    name: z.string().catch(''),
    status: z.string().catch(''),
    effectiveStatus: nstr,
    running: z.boolean().catch(false),
    dailyBudgetMinor: nnum,
    lifetimeBudgetMinor: nnum,
    spend: Spend2.catch({ todayMinor: null, yesterdayMinor: null }),
    managedByLaunch: AdsManagedByLaunch,
    ads: z.array(AdsCampaignAd).catch([]),
    purchases: PurchasesDay,
    ...DayFields,
    allChildrenPaused: nbool(false),
});
export const AdsCampaignChildren = z.object({
    adSets: z.array(AdsCampaignAdSet),
    lastSyncedAt: nstr,
});
export const AdsCampaignSyncResponse = z.object({
    row: AdsCampaignRow,
    children: AdsCampaignChildren,
    fromCache: z.boolean().optional(),
});
// --- проверка перед действием -----------------------------------------------
export const ADS_OPS_LEVELS = ['campaigns', 'adsets', 'ads'];
export const AdsOpsLevelPath = z.enum(ADS_OPS_LEVELS);
export const AdsStatusCheckQuery = z.object({ action: z.enum(['pause', 'enable']) });
const Rule = z.object({
    ruleId: z.string(),
    name: z.string().catch(''),
    kind: z.string().catch(''),
    effect: z.string().catch('other'),
    conflict: z.boolean().catch(false),
});
const Pending = z.object({
    kind: z.string().catch('rule'),
    id: z.string().catch(''),
    ruleId: z.string().optional().catch(undefined),
    at: nstr,
    description: z.string().catch(''),
});
const StatusPair = z.object({ status: nstr, effectiveStatus: nstr });
const ChildLite = z.object({
    id: z.string(),
    name: z.string().catch(''),
    status: nstr,
    effectiveStatus: nstr,
});
export const AdsStatusCheck = z.object({
    level: z.enum(['campaign', 'adset', 'ad']),
    entityId: z.string(),
    name: z.string().catch(''),
    campaignId: nstr,
    adSetId: nstr,
    status: nstr,
    effectiveStatus: nstr,
    parents: z
        .object({
        campaign: StatusPair.nullish().transform((v) => v ?? null),
        adset: StatusPair.nullish().transform((v) => v ?? null),
    })
        .catch({ campaign: null, adset: null }),
    managedByLaunch: AdsManagedByLaunch,
    action: z.enum(['pause', 'enable']),
    noop: z.boolean().catch(false),
    willDeliver: z.boolean().catch(false),
    spend: z
        .object({
        budgetLevel: z.string().catch(''),
        dailyBudgetMinor: nnum,
        lifetimeBudgetMinor: nnum,
        currency: z.string().catch(''),
        note: z.string().catch(''),
    })
        .nullish()
        .transform((v) => v ?? null),
    rules: z.array(Rule).catch([]),
    pendingEnables: z.array(Pending).catch([]),
    grace: z
        .object({
        exceptionId: z.string().catch(''),
        graceUsdMinor: nnum,
        spentMinor: nnum,
    })
        .nullish()
        .transform((v) => v ?? null),
    warnings: z.array(z.object({ code: z.string(), message: z.string() })).catch([]),
    children: z
        .object({
        adSets: z
            .array(ChildLite)
            .nullish()
            .transform((v) => v ?? null),
        ads: z
            .array(ChildLite)
            .nullish()
            .transform((v) => v ?? null),
        truncated: z.boolean().catch(false),
    })
        .nullish()
        .transform((v) => v ?? null),
});
// --- пауза / включение ------------------------------------------------------
export const AdsCampaignPauseRequest = z.object({
    reason: z.string().trim().min(1).max(ADS_CAMPAIGN_REASON_MAX).optional(),
    pendingEnables: z.enum(['cancel', 'keep']).optional(),
});
export const AdsCampaignEnableRequest = z.object({
    graceUsd: z.number().positive().max(ADS_CAMPAIGNS_GRACE_MAX_USD).optional(),
    reason: z.string().trim().min(1).max(ADS_CAMPAIGN_REASON_MAX).optional(),
});
const ResultBase = {
    campaignId: nstr,
    adSetId: nstr,
    adId: nstr,
    name: nstr,
    status: nstr,
    effectiveStatus: nstr,
};
const Warning = z.object({ code: z.string(), message: z.string() });
/** Делегирование запуску конструктора (`contracts-launch-ops.md`, 03.11): действие выполнил запуск, а не общий маршрут. */
const Delegation = {
    delegatedToLaunch: z.boolean().catch(false),
    launchId: nstr,
    /** 202: запуск ещё выполняет действие — статус обновится через минуту. */
    pending: z.boolean().catch(false),
};
/** Что стало с ждущими включениями после паузы (форма за K1; `note` — EN-заметка, мапим на русский). */
const PendingEnablesOutcome = z
    .object({
    action: z.enum(['cancel', 'keep']).catch('keep'),
    found: z.array(Pending).catch([]),
    cancelledIds: z.array(z.string()).catch([]),
    note: z.string().optional(),
})
    .optional()
    .catch(undefined);
export const AdsCampaignPauseResult = z.object({
    ...ResultBase,
    ...Delegation,
    alreadyPaused: z.boolean().catch(false),
    /** Кампания ждала автозапуска — он отменён. */
    autoStartCancelled: z.boolean().catch(false),
    pendingEnables: PendingEnablesOutcome,
    /** Неблокирующие предупреждения (таймер правила включит объект снова и т. п.); кривой вид — [], не 502. */
    warnings: z.array(Warning).optional().catch([]),
});
export const AdsCampaignEnableResult = z.object({
    ...ResultBase,
    ...Delegation,
    alreadyActive: z.boolean().catch(false),
    graceExceptionId: nstr,
    warnings: z.array(z.object({ code: z.string(), message: z.string() })).catch([]),
    ruleConflicts: z.array(Rule).catch([]),
});
// --- пачка (только кампании) ------------------------------------------------
const BulkIds = z
    .array(AdsEntityId)
    .min(1)
    .max(ADS_CAMPAIGNS_BULK_MAX)
    .transform((ids) => Array.from(new Set(ids)));
export const AdsBulkPauseRequest = z.object({
    level: z.literal('campaign'),
    ids: BulkIds,
    reason: z.string().trim().min(1).max(ADS_CAMPAIGN_REASON_MAX).optional(),
    pendingEnables: z.enum(['cancel', 'keep']),
});
export const AdsBulkEnablePreviewRequest = z.object({
    level: z.literal('campaign').default('campaign'),
    ids: BulkIds,
});
export const AdsBulkEnableRequest = z.object({
    level: z.literal('campaign'),
    ids: BulkIds,
    reason: z.string().trim().min(1).max(ADS_CAMPAIGN_REASON_MAX).optional(),
    confirmToken: z.string().min(10).max(300),
});
export const AdsBulkPreviewItem = z.object({
    id: z.string(),
    name: z.string(),
    ok: z.boolean(),
    /** Почему кампанию не включить пачкой (запуск конструктора, нет в Meta, ошибка). */
    skipReason: nstr,
    noop: z.boolean(),
    willDeliver: z.boolean(),
    dailyMinor: nnum,
    note: z.string(),
    currency: z.string(),
    rules: z.array(Rule),
    warnings: z.array(z.object({ code: z.string(), message: z.string() })),
    pendingEnables: z.array(Pending),
});
export const AdsBulkEnablePreview = z.object({
    items: z.array(AdsBulkPreviewItem),
    /** Сумма в день по кампаниям, которые будут включены (одна валюта кабинета). */
    totalDailyMinor: z.number(),
    currency: z.string(),
    confirmToken: z.string(),
    expiresAt: z.string(),
});
export const AdsBulkResultItem = z.object({
    id: z.string(),
    name: nstr,
    ok: z.boolean(),
    code: nstr,
    /** Русская фраза (без кодов и JSON). */
    message: nstr,
    alreadyPaused: z.boolean().optional(),
    alreadyActive: z.boolean().optional(),
    skipped: z.boolean().optional(),
    /** Кампания запуска: запуск ещё выполняет действие (202). */
    pending: z.boolean().optional(),
    /** Запуск кампании, по которому отказ (409 `launch-*`): ссылка «Открыть запуск». */
    launchId: z.string().optional(),
    /** Кампания ждала автозапуска — он отменён. */
    autoStartCancelled: z.boolean().optional(),
    /** Неблокирующие предупреждения объекта — словами через маппер; кривой вид — []. */
    warnings: z.array(Warning).optional().catch([]),
});
export const AdsBulkResult = z.object({
    bulkId: z.string(),
    items: z.array(AdsBulkResultItem),
});
export { AdsId as AdsCampaignAccountId };
//# sourceMappingURL=ads-campaigns.js.map